/**
 * Product-event taxonomy barrel (spec 68 §6.6.4/§6.6.5).
 *
 * Each domain file exports `{ <event>: { schema, schemaVer } }`; this file
 * merges them into one `eventSchemas` map. A NEW domain lands as a new
 * file + one import line here — no existing file is edited. That is the
 * property that makes the taxonomy scale (§6.6.4).
 */
import type { z } from 'zod';
import { accountEvents } from './account.js';
import { agentEvents } from './agent.js';
import { agentRunEvents } from './agent-run.js';
import { billingEvents } from './billing.js';
import { connectionEvents } from './connection.js';
import { errorEvents } from './error.js';
import { mcpEvents } from './mcp.js';
import { modelEvents } from './model.js';
import { projectEvents } from './project.js';
import { skillEvents } from './skill.js';
import { threadEvents } from './thread.js';
import { userEvents } from './user.js';
import { waitlistEvents } from './waitlist.js';

export const eventSchemas = {
  ...accountEvents,
  ...projectEvents,
  ...agentEvents,
  ...agentRunEvents,
  ...threadEvents,
  ...connectionEvents,
  ...skillEvents,
  ...modelEvents,
  ...billingEvents,
  ...mcpEvents,
  ...userEvents,
  ...waitlistEvents,
  ...errorEvents,
} as const;

export type ProductEvent = keyof typeof eventSchemas;

export type ProductEventProps<E extends ProductEvent> = z.infer<(typeof eventSchemas)[E]['schema']>;

export { AUDIT_KIND_TO_EVENT } from './agent-run.js';
export { BUDGET_BLOCKED_AUDIT_KIND } from './billing.js';

/**
 * Title-Case an `object.action_past_tense` event for the Amplitude wire
 * boundary (§6.6.1): `agent_run.started` → `Agent Run Started`. Properties
 * stay snake_case unchanged.
 */
export function titleCaseEvent(event: string): string {
  return event
    .split(/[._]/)
    .filter((w) => w.length > 0)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}
